import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Select, Input, DatePicker, TimePicker, message } from 'antd';
import { CalendarOutlined, LinkOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { API_BASE_URL } from "../config/apiconfig";


const { Option } = Select;
const { TextArea } = Input;

// On-site location is auto-resolved by backend (unit/company address); no Mapbox needed

const ScheduleEventModal = ({ visible, onCancel, applicationId, recruiterId, candidateEmail }) => {
    const [templates, setTemplates] = useState([]);
    const [docTemplates, setDocTemplates] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    // Location is auto-resolved by backend for On-site events
    
    const [formData, setFormData] = useState({
        template_id: null,
        event_name: '',
        event_type: 'Virtual',
        event_url: '',
        event_location: '',
        duration: 60,
        scheduled_time: dayjs().add(1, 'day').set('hour', 10).set('minute', 0),
        notes: '',
        document_template_id: null,
        email_template_id: null
    });

    const [editDoc, setEditDoc] = useState(false);
    const [editEmail, setEditEmail] = useState(false);
    const [overrideDocumentHtml, setOverrideDocumentHtml] = useState('');
    const [overrideEmailSubject, setOverrideEmailSubject] = useState('');
    const [overrideEmailBody, setOverrideEmailBody] = useState('');

    useEffect(() => {
        if (visible) {
            fetchTemplates();
            fetchDocumentTemplates();
            fetchEmailTemplates();
            // Reset form when modal opens
            setFormData({
                template_id: null,
                event_name: '',
                event_type: 'Virtual',
                event_url: '',
                event_location: '',
                duration: 60,
                scheduled_time: dayjs().add(1, 'day').set('hour', 10).set('minute', 0),
                notes: '',
                document_template_id: null,
                email_template_id: null
            });
            setEditDoc(false);
            setEditEmail(false);
            setOverrideDocumentHtml('');
            setOverrideEmailSubject('');
            setOverrideEmailBody('');
        }
    }, [visible]);

    const fetchTemplates = async () => {
        try {
            console.log('Fetching templates for recruiter:', recruiterId);
            const response = await axios.get(`${API_BASE_URL}/api/templates/events?created_by=${recruiterId}`);
            console.log('Templates response:', response.data);
            setTemplates(response.data);
        } catch (error) {
            console.error('Failed to load event templates:', error);
            console.error('Error response:', error.response?.data);
            message.error('Failed to load event templates');
        }
    };

    const fetchDocumentTemplates = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/templates/documents?created_by=${recruiterId}&category=call_for_interview`);
            setDocTemplates(res.data || []);
        } catch (e) {
            console.error('Failed to load document templates', e);
        }
    };

    const fetchEmailTemplates = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/templates/emails?created_by=${recruiterId}`);
            setEmailTemplates(res.data || []);
        } catch (e) {
            console.error('Failed to load email templates', e);
        }
    };

    const handleTemplateChange = (templateId) => {
        console.log('Selected template ID:', templateId);
        const selectedTemplate = templates.find(t => t.id === templateId);
        console.log('Selected template:', selectedTemplate);
        
        if (selectedTemplate) {
            setFormData(prev => ({
                ...prev,
                template_id: templateId,
                event_name: selectedTemplate.title,
                event_type: selectedTemplate.event_type || 'Virtual',
                duration: selectedTemplate.duration || 60,
                notes: selectedTemplate.description || ''
            }));
        }
    };

    const handleDocTemplateChange = (id) => {
        setFormData(prev => ({ ...prev, document_template_id: id }));
        if (editDoc) {
            const tpl = docTemplates.find(d => d.id === id);
            setOverrideDocumentHtml(tpl?.body_html || '');
        }
    };

    const handleEmailTemplateChange = (id) => {
        setFormData(prev => ({ ...prev, email_template_id: id }));
        if (editEmail) {
            const tpl = emailTemplates.find(e => e.id === id);
            setOverrideEmailSubject(tpl?.subject || '');
            setOverrideEmailBody(tpl?.content || '');
        }
    };

    // No location selection UI for On-site; backend resolves from recruiter unit/company

    const handleSubmit = async () => {
    console.log('Application ID:', applicationId);
    console.log('=== FORM SUBMISSION START ===');
    console.log('Form data:', formData);
    console.log('Application ID:', applicationId);
    console.log('Recruiter ID:', recruiterId);
    console.log('Candidate Email:', candidateEmail);

    // Enhanced validation
    if (!formData.event_name || formData.event_name.trim() === '') {
        message.error('Event name is required');
        return;
    }

    if (formData.event_type === 'Virtual' && (!formData.event_url || formData.event_url.trim() === '')) {
        message.error('Event URL is required for virtual events');
        return;
    }

    // For On-site events, location is auto-resolved by backend; no client validation

    if (!formData.scheduled_time || !formData.scheduled_time.isValid()) {
        message.error('Please select a valid date and time');
        return;
    }

    if (!applicationId) {
        message.error('Application ID is missing');
        return;
    }

    if (!recruiterId) {
        message.error('Recruiter ID is missing');
        return;
    }

    // Prepare payload - ADD created_by field
    const payload = {
        application_id: applicationId,
        template_id: formData.template_id,
        event_name: formData.event_name.trim(),
        event_type: formData.event_type,
        event_url: formData.event_type === 'Virtual' ? formData.event_url.trim() : null,
        event_location: null,
        duration: parseInt(formData.duration) || 60,
        scheduled_time: formData.scheduled_time.format('YYYY-MM-DD HH:mm:ss'),
        notes: formData.notes ? formData.notes.trim() : '',
        created_by: recruiterId,  // ADD THIS LINE
        document_template_id: formData.document_template_id,
        email_template_id: formData.email_template_id,
        override_document_html: editDoc ? overrideDocumentHtml : undefined,
        override_email_subject: editEmail ? overrideEmailSubject : undefined,
        override_email_body: editEmail ? overrideEmailBody : undefined
    };

    console.log('Payload to send:', payload);

    setLoading(true);
    try {
        console.log('Sending request to /api/events...');
        const response = await axios.post(`${API_BASE_URL}/api/events`, payload, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        
        console.log('Response received:', response.data);
        message.success('Event scheduled successfully!');
        onCancel(true); // Pass true to indicate success
    } catch (error) {
        console.error('=== ERROR DETAILS ===');
        console.error('Error object:', error);
        console.error('Error message:', error.message);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        console.error('Error headers:', error.response?.headers);
        
        // More specific error messages
        if (error.response?.status === 401) {
            message.error('Authentication failed. Please login again.');
        } else if (error.response?.status === 404) {
            message.error('Application not found. Please refresh and try again.');
        } else if (error.response?.status === 400) {
            message.error(`Invalid data: ${error.response.data?.message || 'Please check your inputs'}`);
        } else if (error.response?.status === 500) {
            message.error(`Server error: ${error.response.data?.message || 'Please try again later'}`);
        } else if (error.code === 'ECONNREFUSED') {
            message.error('Cannot connect to server. Please check if the backend is running.');
        } else {
            message.error(`Failed to schedule event: ${error.response?.data?.message || error.message}`);
        }
    } finally {
        setLoading(false);
    }
};

    return (
        <Modal
            title="Schedule New Event"
            visible={visible}
            onCancel={() => onCancel(false)}
            footer={[
                <Button key="cancel" onClick={() => onCancel(false)}>
                    Cancel
                </Button>,
                <Button 
                    key="submit" 
                    type="primary" 
                    loading={loading}
                    onClick={handleSubmit}
                >
                    Schedule Event
                </Button>
            ]}
            width={700}
        >
            <div className="space-y-4">
                <div>
                    <label className="block mb-1 font-medium">Select an event template</label>
                    <Select
                        className="w-full"
                        placeholder="Select template"
                        onChange={handleTemplateChange}
                        value={formData.template_id}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        allowClear
                    >
                        {templates.map(template => (
                            <Option key={template.id} value={template.id}>
                                {template.title} ({template.event_type || 'Virtual'})
                            </Option>
                        ))}
                    </Select>
                </div>

                <div>
                    <label className="block mb-1 font-medium">Document template (PDF)</label>
                    <Select
                        className="w-full"
                        placeholder="Select document template"
                        onChange={handleDocTemplateChange}
                        value={formData.document_template_id}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                    >
                        {docTemplates.map(t => (
                            <Option key={t.id} value={t.id}>{t.title}</Option>
                        ))}
                    </Select>
                    <div className="mt-2 flex items-center gap-2">
                        <input type="checkbox" id="editDoc" checked={editDoc} onChange={(e) => {
                            setEditDoc(e.target.checked);
                            if (e.target.checked) {
                                const tpl = docTemplates.find(d => d.id === formData.document_template_id);
                                setOverrideDocumentHtml(tpl?.body_html || '');
                            }
                        }} />
                        <label htmlFor="editDoc">Edit document before sending</label>
                    </div>
                    {editDoc && (
                        <div className="mt-2">
                            <TextArea rows={6} value={overrideDocumentHtml} onChange={(e) => setOverrideDocumentHtml(e.target.value)} placeholder="HTML body (supports {{placeholders}})" />
                        </div>
                    )}
                </div>

                <div>
                    <label className="block mb-1 font-medium">Email template</label>
                    <Select
                        className="w-full"
                        placeholder="Select email template"
                        onChange={handleEmailTemplateChange}
                        value={formData.email_template_id}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                    >
                        {emailTemplates.map(t => (
                            <Option key={t.id} value={t.id}>{t.title}</Option>
                        ))}
                    </Select>
                    <div className="mt-2 flex items-center gap-2">
                        <input type="checkbox" id="editEmail" checked={editEmail} onChange={(e) => {
                            setEditEmail(e.target.checked);
                            if (e.target.checked) {
                                const tpl = emailTemplates.find(em => em.id === formData.email_template_id);
                                setOverrideEmailSubject(tpl?.subject || '');
                                setOverrideEmailBody(tpl?.content || '');
                            }
                        }} />
                        <label htmlFor="editEmail">Edit email before sending</label>
                    </div>
                    {editEmail && (
                        <div className="mt-2 space-y-2">
                            <Input placeholder="Email subject" value={overrideEmailSubject} onChange={(e) => setOverrideEmailSubject(e.target.value)} />
                            <TextArea rows={6} placeholder="Email HTML body" value={overrideEmailBody} onChange={(e) => setOverrideEmailBody(e.target.value)} />
                        </div>
                    )}
                </div>

                <div>
                    <label className="block mb-1 font-medium">Event name *</label>
                    <Input
                        value={formData.event_name}
                        onChange={e => setFormData({...formData, event_name: e.target.value})}
                        placeholder="Enter event name"
                        required
                    />
                </div>

                <div>
                    <label className="block mb-1 font-medium">Event type</label>
                    <Select
                        className="w-full"
                        value={formData.event_type}
                        onChange={type => setFormData({...formData, event_type: type, event_url: '', event_location: ''})}
                    >
                        <Option value="Virtual">Virtual</Option>
                        <Option value="On-site">On-site</Option>
                    </Select>
                </div>

                {formData.event_type === 'Virtual' ? (
                    <div>
                        <label className="block mb-1 font-medium">
                            <LinkOutlined className="mr-2" />
                            Event URL * (eg: Zoom, Google Meets, etc.)
                        </label>
                        <Input
                            value={formData.event_url}
                            onChange={e => setFormData({...formData, event_url: e.target.value})}
                            placeholder="https://zoom.us/j/123456789"
                            required
                        />
                    </div>
                ) : (
                    <div className="p-3 rounded bg-gray-50 text-gray-700 text-sm">
                        On-site location will be set automatically to your unit address (if available) or company address.
                    </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block mb-1 font-medium">Duration (minutes)</label>
                        <Input
                            type="number"
                            value={formData.duration}
                            onChange={e => setFormData({...formData, duration: parseInt(e.target.value) || 60})}
                            min="15"
                            max="480"
                        />
                    </div>
                    <div>
                        <label className="block mb-1 font-medium">Date *</label>
                        <DatePicker
                            className="w-full"
                            value={formData.scheduled_time}
                            onChange={date => {
                                if (date) {
                                    // Preserve the time when changing date
                                    const newDateTime = date
                                        .set('hour', formData.scheduled_time.hour())
                                        .set('minute', formData.scheduled_time.minute());
                                    setFormData({...formData, scheduled_time: newDateTime});
                                }
                            }}
                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                        />
                    </div>
                </div>

                <div>
                    <label className="block mb-1 font-medium">Time *</label>
                    <TimePicker
                        className="w-full"
                        format="HH:mm"
                        value={formData.scheduled_time}
                        onChange={time => {
                            if (time) {
                                // Preserve the date when changing time
                                const newDateTime = formData.scheduled_time
                                    .set('hour', time.hour())
                                    .set('minute', time.minute());
                                setFormData({...formData, scheduled_time: newDateTime});
                            }
                        }}
                        minuteStep={15}
                    />
                </div>

                <div>
                    <label className="block mb-1 font-medium">Notes</label>
                    <TextArea
                        rows={3}
                        value={formData.notes}
                        onChange={e => setFormData({...formData, notes: e.target.value})}
                        placeholder="Additional notes about the event..."
                    />
                </div>
            </div>
        </Modal>
    );
};

// Debounce utility function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

export default ScheduleEventModal;